import React, { useContext, useEffect, useRef, useState } from "react";
import { getAbsConstraintsByAttributeID, getCFConstraintsByAttributeID } from "api/Attributes";
import { AttributeContext } from "../AttributeContext";
import { toast } from 'Components/DesignSystem/Toast/useToast'
import ViewMoreButton from "Components/Common/components/ViewMoreButton";
import { ABS_ACTIVATE_MESSAGE, ABS_SELECTION, ABS_VALUE, CF_ACTIVATE_MESSAGE, CF_VALUE } from "../AttributeConstants";
import Accordion from "Components/DesignSystem/Accordion/Accordion";
import Alert from "Components/DesignSystem/Alert/Alert";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";
import Typography from "Components/DesignSystem/Typography/Typography";
import Toggle from "Components/DesignSystem/Toggle/Toggle";
import TextField from "Components/DesignSystem/TextField/TextField";
import Search from "Components/DesignSystem/Search/Search";
import Table from "Components/DesignSystem/Table/Table";

const SEARCH_DEBOUNCE_MS = 600;

/**
 * Read-only ABS / CF accordion. Fetch logic is verbatim — the fetch-on-mount
 * [search] effect, the [editingItem.id, search] reset, the collapse guard and
 * the `(prev || !reset)` append quirk; only the rendering moved onto DS
 * Accordion / Typography / Table / disabled Toggle+TextField rows, with the
 * module loading grammar (skeleton for an empty load, inline spinner for a
 * view-more append) and the CustomSearchBar's 600 ms debounce living here.
 */
export default function ABSView({title, id}) {
  const { editingItem } = useContext(AttributeContext);

  const [collapse, setCollapse] = useState(false);
  const [loading, setLoading] = useState({ status: "", message: "", });

  let [absDetails, setAbsDetails] = useState(null);
  let [attributeValuesCount, setAttributeValuesCount] = useState(0);
  let [valuesConstraints, setValueConstraints] = useState([]);
  const [search, setSearch] = useState("")

  const attrId = useRef(null)

  const [searchInput, setSearchInput] = useState("");
  /*
    § 288 — the SEARCH half of `loading.status`, kept apart from the view-more half.
    That one status drives both, and a field that spun when the user clicked "View
    More" would be claiming a search nobody asked for. `skip === undefined` is the
    discriminator the fetchers already take: a search or a first load passes none, an
    append passes the current length. Presentational — nothing reads it but the field.
  */
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceRef = useRef(null);
  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);
  const handleSearchInput = (value) => {
    const trimmed = value.trimStart();
    setSearchInput(trimmed);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearch(trimmed), SEARCH_DEBOUNCE_MS);
  };

  function handle_collapse() {
    setCollapse(!collapse);
  }

  const getAbsValues = (search, skip, reset) => {
    attrId.current = editingItem.id
    setLoading({status: "Loading", message: "Loading..."})
    if (skip === undefined) setSearchLoading(true)

      getAbsConstraintsByAttributeID(editingItem.id, 10, skip, search)
      .then((res) => {
        const {value_constraints, ...rest} = res.constraints
        setAbsDetails(rest)
        if(value_constraints) setValueConstraints((prev) => {
          return (prev || !reset) ? [...prev, ...value_constraints] : value_constraints
        })
        setAttributeValuesCount(res.count)
        setLoading({
          status: "Success",
          message: ""
        })
      })
      .catch(err => {
        toast.error(err.message)
        setLoading({
          status: "Error",
          message: err.message
        })
      })
      .finally(() => setSearchLoading(false))
  }

  const getCfValues = (search, skip, reset) => {
    attrId.current = editingItem.id
    setLoading({status: "Loading", message: "Loading..."})
    if (skip === undefined) setSearchLoading(true)
    getCFConstraintsByAttributeID(editingItem.id, 10, skip, search)
      .then((res) => {
        const {value_constraints, ...rest} = res.constraints
        setAbsDetails(rest)
        if(value_constraints) setValueConstraints((prev) => {
          return (prev || !reset) ? [...prev, ...value_constraints] : value_constraints
        })
        setAttributeValuesCount(res.count)
        setLoading({
          status: "Success",
          message: ""
        })
      })
      .catch(err => {
        toast.error(err.message)
        setLoading({
          status: "Error",
          message: err.message
        })
      })
      .finally(() => setSearchLoading(false))
  }

  useEffect(() => {
    if (title === ABS_SELECTION) {
      getAbsValues(search, undefined, true)
    } else {
      getCfValues(search, undefined, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    setAbsDetails(null)
    setAttributeValuesCount(0)
    setValueConstraints([])
  }, [editingItem.id, search])

  useEffect(() => {
    if(!collapse || attrId.current === editingItem.id) return
    setAbsDetails(null);
    setAttributeValuesCount(0);
    setValueConstraints([]);
    if (title === ABS_SELECTION) {
      getAbsValues();
    } else {
      getCfValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapse])


  const ABSDetailItem = ({ label, value, col = 6 }) => {
    return (
      <div className={`col-${col}`}>
        <Typography variant="body-primary" as="p" className="tw-mb-0 tw-text-ink">
          {`${label}`}
        </Typography>
        <Typography variant="body-primary" as="p" className="tw-mb-0 tw-text-ink-secondary">
          {`${value}`}
        </Typography>
      </div>
    );
  };

  function getBool(val) {
    return !!JSON.parse(String(val).toLowerCase());
  }

  const isAbs = title === ABS_SELECTION;
  const columns = [
    {
      id: "show",
      header: "Display",
      accessor: isAbs ? "abs_show" : "cf_show",
      render: (_value, singleValue) => (
        <Toggle
          label=""
          aria-label={`Show ${singleValue.value}`}
          data-testid="componets-absview-input-1"
          disabled={true}
          checked={getBool(isAbs ? singleValue.abs_show : singleValue.cf_show)}
          onChange={() => null}
        />
      ),
    },
    { id: "value", header: "Value", accessor: "value" },
    {
      id: "display_value",
      header: isAbs ? ABS_VALUE : CF_VALUE,
      accessor: isAbs ? "abs_display_value" : "cf_display_value",
      render: (_value, singleValue) => (
        <TextField
          label=""
          size="sm"
          type="text"
          disabled={true}
          data-testid="attribute-abs-display-value-input"
          name="abs_display_value"
          value={(isAbs ? singleValue.abs_display_value : singleValue.cf_display_value) ?? ""}
          placeholder={isAbs ? ABS_VALUE : CF_VALUE}
          onChange={() => null}
        />
      ),
    },
    ...(isAbs
      ? [
          {
            id: "abs_image_url",
            header: "ABS Image Url",
            accessor: "abs_image_url",
            render: (_value, singleValue) => (
              <TextField
                label=""
                size="sm"
                type="text"
                disabled={true}
                data-testid="componets-absview-input-2"
                name="abs_image_url"
                value={singleValue.abs_image_url ?? ""}
                placeholder="ABS Image Url starts with https:// or http://"
                onChange={() => {}}
              />
            ),
          },
          {
            id: "abs_alt_text",
            header: "ABS Alt text",
            accessor: "abs_alt_text",
            render: (_value, singleValue) => (
              <TextField
                label=""
                size="sm"
                type="text"
                disabled={true}
                data-testid="componets-absview-input-3"
                name="abs_alt_text"
                value={singleValue.abs_alt_text ?? ""}
                placeholder="ABS Alt text"
                onChange={() => null}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Accordion
      title={title}
      open={collapse}
      onOpenChange={() => handle_collapse()}
      data-testid="componets-absview-div-1"
      variant="boxed"
    >
      <div className="tw-pb-4">
        {/* Module loading grammar (user-requested 2026-09-01): skeleton for a
            load with nothing on screen; an append keeps the content mounted. */}
        {loading.status === "Loading" && !absDetails && (
          <div className="tw-space-y-3" data-testid="attribute-abs-view-skeleton">
            <Skeleton variant="rect" width="30%" height="20px" />
            <Skeleton variant="rect" width="100%" height="20px" />
            <Skeleton variant="rect" width="100%" height="20px" />
            <Skeleton variant="rect" width="60%" height="20px" />
          </div>
        )}

        {loading.status === "Error" && (
          <Alert tone="danger" live data-testid="attribute-abs-view-status">
            {loading.message}
          </Alert>
        )}

        {(loading.status === "Success" ||
          (loading.status === "Loading" && absDetails)) && absDetails && (
          <div className="tw-border tw-border-edge tw-rounded-ctl tw-p-4">
            <div className="container overflow-hidden">
              <div className="row gy-3">
                <ABSDetailItem
                  col={12}
                  label={title === ABS_SELECTION ? ABS_ACTIVATE_MESSAGE : CF_ACTIVATE_MESSAGE}
                  value={`${ title === ABS_SELECTION ? absDetails.abs_enabled ? "Yes" : "No" : absDetails.cf_enabled ? "Yes" : "No" }`}
                />
                <ABSDetailItem
                  col={12}
                  label="Display Name"
                  value={`${ title === ABS_SELECTION ?  absDetails?.abs_properties?.displayName ?? "-" : absDetails?.cf_properties?.displayName ?? "-" }`}
                />
                <ABSDetailItem
                  col={4}
                  label="Display Search"
                  value={`${ title === ABS_SELECTION ? absDetails.abs_properties && absDetails.abs_properties.displaySearch ? "Yes" : "No" : absDetails.cf_properties && absDetails.cf_properties.displaySearch ? "Yes" : "No"}`}
                />
                <ABSDetailItem
                  col={4}
                  label="Values per Row"
                  value={`${ title === ABS_SELECTION ? absDetails.abs_properties && absDetails.abs_properties.valuesPerRow :  absDetails.cf_properties && absDetails.cf_properties.valuesPerRow }`}
                />
                <ABSDetailItem
                  col={4}
                  label="Max Height"
                  value={`${ title === ABS_SELECTION ? absDetails.abs_properties && absDetails.abs_properties.maxHeight : absDetails.cf_properties && absDetails.cf_properties.maxHeight }`}
                />
                <ABSDetailItem
                  col={12}
                  label="Display Type"
                  value={`${ title === ABS_SELECTION ? absDetails.abs_properties && absDetails.abs_properties.DisplayType : absDetails.cf_properties && absDetails.cf_properties.DisplayType}`}
                />
                <div>
                  <Search
                    size="sm"
                    className="tw-w-60"
                    placeholder="Search Attribute Values"
                    ariaLabel="Search attribute values"
                    data-testid="attribute-abs-view-search-input"
                    value={searchInput}
                    onChange={handleSearchInput}
                    /* § 288 — the search-only flag, plus the debounce in front of it.
                       The view-more append keeps its own spinner below the table. */
                    loading={searchLoading || searchInput !== search}
                    loadingLabel="Searching values"
                  />
                </div>
                {valuesConstraints &&
                  valuesConstraints.length > 0 && (
                    <Table
                      data={valuesConstraints}
                      rowKey={(row) => row.value}
                      data-testid="attribute-abs-view-table"
                      columns={columns}
                    />
                  )}
                {loading.status === "Loading" ? (
                  <span
                    className="tw-inline-flex tw-items-center tw-px-2"
                    data-testid="attribute-abs-view-loading-more"
                  >
                    <Spinner size="sm" label="Loading more values" />
                  </span>
                ) : (
                  <ViewMoreButton
                    condition = {valuesConstraints.length < attributeValuesCount}
                    state = {valuesConstraints.length >= attributeValuesCount}
                    handleClick = {() => title === ABS_SELECTION ?  getAbsValues(search, valuesConstraints.length) : getCfValues(search, valuesConstraints.length)}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Accordion>
  );
}
